import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogComponent } from '@theme/dialog';

/** Explicit size confirmation for the redesigned PDP; leaves Shopify variant resolution intact. */
class PdpPurchase extends Component {
  #controller;
  #sizeChosen = false;
  /** @type {{ id: string | number, available: boolean, productId: string, label: string } | undefined} */
  #resolvedVariant;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    // A fresh page load always starts without a size, even when the URL carries ?variant= (product
    // cards link that way to keep the card's color). Linked-product morphs use refreshSelection.
    this.addEventListener('change', this.#handleChange, { signal, capture: true });
    this.addEventListener('submit', this.#handleSubmit, { signal, capture: true });
    this.addEventListener('click', this.#handleSizeGuide, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleUpdate, { signal });
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.refreshSelection();
  }

  /**
   * Re-reads `data-has-selected-variant` after a linked-product morph, which may deliberately
   * require a fresh size choice. Called by variant-picker because morph's update hook receives
   * the parsed (non-upgraded) node, so updatedCallback does not run for full-main morphs.
   */
  refreshSelection() {
    this.#sizeChosen = this.dataset.hasSelectedVariant === 'true';
    this.#resolvedVariant = undefined;
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    this.#resolvedVariant = undefined;
    if (event.target instanceof HTMLInputElement && event.target.closest('[data-option-kind="size"]')) {
      this.#sizeChosen = true;
      // Grey out colors right away instead of waiting for the variant re-render.
      this.#syncColorAvailability();
    }
  };

  /** @param {Event} event */
  #handleSubmit = (event) => {
    if (!this.#needsSize()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const input = this.querySelector('[data-option-kind="size"] input');
    if (input instanceof HTMLInputElement) input.focus();
  };

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #handleUpdate = (event) => {
    const form = this.querySelector('product-form-component');
    const resource = event.detail.resource;
    const sourceForm = Array.from(event.detail.data.html.querySelectorAll('product-form-component')).find(
      (candidate) => candidate instanceof HTMLElement && candidate.dataset.productId === event.detail.data.productId
    );
    if (form instanceof HTMLElement && form.dataset.productId === event.detail.data.productId) {
      this.#resolvedVariant = {
        id: resource?.id ?? '',
        available: resource?.available === true,
        productId: event.detail.data.productId,
        label: sourceForm?.querySelector('.add-to-cart-text__content')?.textContent?.trim() ?? '',
      };
    }
    // ProductForm handles the same bubbling event first and may replace its button.
    queueMicrotask(() => { if (this.isConnected) this.#sync(); });
  };

  #needsSize() {
    return !this.#sizeChosen && !!this.querySelector('[data-option-kind="size"]');
  }

  /** Opens the existing merchant-authored sizing dialog without navigating. @param {MouseEvent} event */
  #handleSizeGuide = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-pdp-size-guide-link]')) return;
    const dialog = this.querySelector('dialog-component[data-pdp-size-guide]');
    if (!(dialog instanceof DialogComponent)) return;
    event.preventDefault();
    dialog.showDialog();
  };

  #sync() {
    const needsSize = this.#needsSize();
    this.toggleAttribute('data-needs-size', needsSize);
    const form = this.querySelector('product-form-component');
    const button = this.querySelector('button[ref="addToCartButton"]');
    if (this.#resolvedVariant && form instanceof HTMLElement && form.dataset.productId === this.#resolvedVariant.productId && button instanceof HTMLButtonElement) {
      const variantId = form.querySelector('input[name="id"]');
      if (variantId instanceof HTMLInputElement) variantId.value = String(this.#resolvedVariant.id);
      button.disabled = needsSize || !this.#resolvedVariant.available;
      if (this.#resolvedVariant.label) this.#setButtonLabel(button, this.#resolvedVariant.label);
    }
    if (needsSize && button instanceof HTMLButtonElement) {
      button.disabled = true;
      this.#setButtonLabel(button, this.dataset.selectSizeLabel ?? '');
    }
    const accelerated = this.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (needsSize) accelerated?.setAttribute('hidden', '');
    for (const input of this.querySelectorAll('[data-option-kind="size"] input')) {
      if (needsSize && input instanceof HTMLInputElement) {
        input.checked = false;
        input.dataset.currentChecked = 'false';
        input.dataset.previousChecked = 'false';
      }
    }
    this.#syncColorAvailability();
  }

  /**
   * Writes the label into the innermost span so the button keeps its nested markup. The pre-order
   * app hides `.add-to-cart-text__content > span` and draws its own label with ::after; flattening
   * the spans would leave both visible.
   * @param {HTMLButtonElement} button
   * @param {string} label
   */
  #setButtonLabel(button, label) {
    const content = button.querySelector('.add-to-cart-text__content');
    if (!content) return;
    const target = content.querySelector(':scope > span > span') ?? content.querySelector(':scope > span');
    if (target) {
      target.textContent = label;
    } else {
      content.replaceChildren(Object.assign(document.createElement('span'), { textContent: label }));
    }
  }

  /**
   * Marks colors with no in-stock variant in the chosen size. Shopify's option availability only
   * reflects options listed earlier, and color comes before size, so read the in-stock variant list.
   * Re-renders reset these marks; #sync reapplies them after every variant update.
   */
  #syncColorAvailability() {
    const sizeInput = this.querySelector('[data-option-kind="size"] input:checked');
    const list = this.querySelector('[data-pdp-available-variants]');

    /** @type {{ options: string[] }[] | null} */
    let available = null;
    if (!this.#needsSize() && sizeInput instanceof HTMLInputElement && list) {
      try {
        available = JSON.parse(list.textContent || '[]');
      } catch {
        available = null;
      }
    }

    const sizeIndex = Number(sizeInput instanceof HTMLInputElement ? sizeInput.dataset.fieldsetIndex : NaN);
    const size = sizeInput instanceof HTMLInputElement ? sizeInput.value : '';

    for (const input of this.querySelectorAll('.variant-option--swatches input')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const colorIndex = Number(input.dataset.fieldsetIndex);
      // Colors linked to another product have no variants in this list.
      const isOwnColor = !input.dataset.connectedProductUrl;
      const soldOutInSize =
        available !== null &&
        isOwnColor &&
        !available.some((variant) => variant.options[colorIndex] === input.value && variant.options[sizeIndex] === size);

      input.toggleAttribute('data-size-unavailable', soldOutInSize);
      // Keep server-marked unavailable colors disabled either way.
      const unavailable = soldOutInSize || input.dataset.optionAvailable === 'false';
      if (unavailable) {
        input.setAttribute('aria-disabled', 'true');
      } else {
        input.removeAttribute('aria-disabled');
      }
    }
  }
}

if (!customElements.get('pdp-purchase-component')) customElements.define('pdp-purchase-component', PdpPurchase);
